import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Checkmark, Missing, Refresh, Warning } from './icons';

export type StatusChipSeverity = 'success' | 'error' | 'warning' | 'info' | 'default';

export interface StatusChipProps {
  severity: StatusChipSeverity;
  text: string;
  /** set false to render text-only chips like the "New" badge */
  icon?: boolean;
}

const bg: Record<StatusChipSeverity, string> = {
  success: '#ECFDF3',
  error: '#FDEFED',
  warning: '#FEEED7',
  info: '#E7F6FE',
  default: '#F0F0F0',
};

const fg: Record<StatusChipSeverity, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
  default: 'text.secondary',
};

// Hex color values for icons (resolved from theme palette)
const fgHex: Record<StatusChipSeverity, string> = {
  success: '#079455',
  error: '#FC4D2B',
  warning: '#F07C29',
  info: '#01ADEF',
  default: '#97A2AA',
};

function statusIcon(severity: StatusChipSeverity) {
  const color = fgHex[severity];
  const sx = { width: '16px', height: '16px', color };
  switch (severity) {
    case 'success':
      return <Checkmark sx={sx} />;
    case 'error':
      return <Missing sx={sx} />;
    case 'warning':
      return <Warning sx={sx} />;
    default:
      return <Refresh sx={sx} />;
  }
}

export function StatusChip({ severity, text, icon = true }: StatusChipProps) {
  return (
    <Box
      sx={{
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: '6px',
        backgroundColor: bg[severity],
      }}
    >
      {icon && statusIcon(severity)}
      <Typography fontSize={12} fontWeight={500} color={fg[severity]} sx={{ textWrap: 'nowrap' }}>
        {text}
      </Typography>
    </Box>
  );
}

/** The "New" article badge (Figma 2871:51292). */
export function NewBadge() {
  return <StatusChip severity="info" text="New" icon={false} />;
}
